import React, { useState, useEffect } from 'react';
import ScanlineOverlay from '../components/ScanlineOverlay';
import { artworks } from '../data/artworks';
import { blogPosts } from '../data/blog';
import { apps } from '../config/apps';

interface PostLine {
  ms: number;
  label: string;
  value: string;
}

/** POST checks — values come from the real data the desktop is about to mount. */
const POST_LINES: PostLine[] = [
  { ms: 120, label: 'memory', value: '65536k ok' },
  { ms: 420, label: 'display', value: 'crt 640x480' },
  { ms: 700, label: 'mount /artworks', value: `${artworks.length} files` },
  { ms: 980, label: 'mount /apps', value: `${apps.length} programs` },
  { ms: 1240, label: 'mount /blog', value: `${blogPosts.length} posts` },
  { ms: 1500, label: 'desktop pet', value: 'awake' },
  { ms: 1760, label: 'window manager', value: 'ok' },
];

const BAR_CELLS = 20;

const BootWindow: React.FC = () => {
  const [done, setDone] = useState(0);
  const [handoff, setHandoff] = useState(false);
  const progress = Math.round((done / POST_LINES.length) * 100);
  const filled = Math.round((done / POST_LINES.length) * BAR_CELLS);

  useEffect(() => {
    const timers = POST_LINES.map(({ ms }, i) =>
      window.setTimeout(() => setDone(i + 1), ms),
    );
    timers.push(window.setTimeout(() => setHandoff(true), 2000));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col items-center justify-center select-none bg-[#09070f] text-green-400">
      <ScanlineOverlay intensity={0.12} vignette />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="flex items-end justify-between border-b border-green-400/15 pb-2 mb-5">
          <h1 className="window-title text-3xl sm:text-4xl text-pink-400 tracking-wide leading-none">
            JMOS
          </h1>
          <p className="font-mono text-[9px] text-green-400/40 pb-0.5">rev 1.04</p>
        </div>

        <div className="space-y-1 font-mono text-[10px] sm:text-[11px]">
          {POST_LINES.map((line, i) => (
            <div
              key={line.label}
              className={`flex items-baseline gap-1 ${i < done ? '' : 'invisible'}`}
            >
              <span className="text-green-400/70">{line.label}</span>
              <span className="flex-1 border-b border-dotted border-green-400/20 translate-y-[-2px]" />
              <span className={i === done - 1 ? 'text-green-300' : 'text-green-400/45'}>
                {line.value}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-6 font-mono text-[10px] sm:text-[11px]">
          <div className="flex items-center gap-2">
            <span className="text-green-400/30">[</span>
            <div className="flex-1 flex gap-px">
              {Array.from({ length: BAR_CELLS }, (_, i) => (
                <span
                  key={i}
                  className={`h-2.5 flex-1 ${
                    i < filled ? 'bg-pink-500' : 'bg-green-400/10'
                  } transition-colors duration-200`}
                />
              ))}
            </div>
            <span className="text-green-400/30">]</span>
            <span className="w-8 text-right text-green-400/45">{progress}%</span>
          </div>

          <p className="mt-4 text-green-300">
            {handoff ? 'starting jmos' : 'post'}
            <span className="ml-0.5 inline-block w-1.5 h-3 bg-green-400/80 animate-pulse align-middle" />
          </p>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 px-6 flex items-center justify-between font-mono text-[9px] text-green-400/30 z-10">
        <span>jimmy eleazar r. mijares</span>
        <span className="hidden sm:inline">cebu, ph</span>
        <span>2026</span>
      </div>
    </div>
  );
};

export default BootWindow;
